import React from 'react';
import { Check, X, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
export const ComparisonSection = () => {
  return <section className="py-20 bg-[#050505] relative overflow-hidden" id="comparison" data-id="element-2161">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#FF0000] to-transparent opacity-50" data-id="element-2162" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#FF0000] to-transparent opacity-50" data-id="element-2163" />

      <div className="max-w-6xl mx-auto px-4 relative z-10" data-id="element-2164">
        <div className="text-center mb-16" data-id="element-2165">
          <div className="inline-block bg-[#FFD700] text-black font-black px-4 py-1 text-xs mb-6 uppercase tracking-widest transform -rotate-2 shadow-lg" data-id="element-2166">
            Von Restorff Effect
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6 uppercase tracking-tight leading-tight" data-id="element-2167">
            Why ChatGPT Is{' '}
            <span className="text-gray-600 line-through decoration-red-600 decoration-4" data-id="element-2168">
              Blind
            </span>{' '}
            To
            <br data-id="element-2169" />
            Your Relationship
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto font-mono leading-relaxed" data-id="element-2170">
            General AI is trained to be polite. We are trained to detect the
            truth.
          </p>
        </div>

        {/* Comparison Table */}
        <div className="overflow-x-auto pb-4" data-id="element-2171">
          <div className="min-w-[700px]" data-id="element-2172">
            {/* Table Header */}
            <div className="grid grid-cols-3 gap-6 mb-6" data-id="element-2173">
              <div className="p-4 flex items-end" data-id="element-2174">
                <span className="text-gray-500 font-mono text-sm uppercase tracking-wider" data-id="element-2175">
                  Feature Comparison
                </span>
              </div>
              <div className="p-6 bg-gray-900/50 rounded-t-xl border-t border-x border-gray-800 text-center" data-id="element-2176">
                <h3 className="text-gray-400 font-bold text-xl uppercase tracking-wide" data-id="element-2177">
                  ChatGPT
                </h3>
                <p className="text-gray-600 text-xs font-mono mt-2" data-id="element-2178">
                  (General AI)
                </p>
              </div>
              <div className="relative p-6 bg-[#FF0000]/10 rounded-t-xl border-t border-x border-[#FF0000] text-center shadow-[0_-10px_30px_rgba(255,0,0,0.15)]" data-id="element-2179">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[#FF0000] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-md" data-id="element-2180">
                  Forensic Grade
                </div>
                <h3 className="text-[#FF0000] font-black text-2xl uppercase tracking-wider filter drop-shadow-[0_0_8px_rgba(255,0,0,0.5)]" data-id="element-2181">
                  ToxicDetector
                </h3>
                <p className="text-[#FF0000]/80 text-xs font-mono mt-2" data-id="element-2182">
                  (Specialized AI)
                </p>
              </div>
            </div>

            {/* Table Rows */}
            <div className="space-y-3" data-id="element-2183">
              <ComparisonRow feature="Tone Detection" competitor="Basic / Formal" us="Slang, Sarcasm & Subtext" highlight data-id="element-2184" />
              <ComparisonRow feature="Focus" competitor="Efficiency" us="Emotional Safety & Validation" highlight data-id="element-2185" />
              <ComparisonRow feature="Output Style" competitor='"I cannot judge relationships"' us='"🚩 92% Toxic. Run."' highlight isQuote data-id="element-2186" />
              <ComparisonRow feature="Social Proof" competitor="None" us="Shareable Viral Meme Cards" highlight data-id="element-2187" />
              <ComparisonRow feature="Privacy" competitor="Data Training" us="100% Anonymous & Encrypted" highlight data-id="element-2188" />
            </div>
          </div>
        </div>

        {/* AI Vision Visual Proof */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.7,
        delay: 0.4
      }} className="mt-20 mb-16 relative max-w-5xl mx-auto" data-id="element-2189">
          <div className="absolute -inset-1 bg-gradient-to-r from-[#FF0000]/20 via-transparent to-[#FF0000]/20 rounded-xl blur-xl opacity-30" data-id="element-2190" />

          <div className="relative bg-black rounded-lg border border-[#FF0000]/30 overflow-hidden shadow-[0_0_60px_rgba(255,0,0,0.15)] group" data-id="element-2191">
            {/* Header Bar */}
            <div className="absolute top-0 left-0 w-full h-10 bg-black/80 backdrop-blur border-b border-[#FF0000]/20 flex items-center justify-between px-4 z-20" data-id="element-2192">
              <div className="flex items-center gap-2" data-id="element-2193">
                <div className="w-2 h-2 rounded-full bg-[#FF0000] animate-pulse" data-id="element-2194" />
                <span className="text-[#FF0000] font-mono text-[10px] tracking-widest uppercase" data-id="element-2195">
                  Live Analysis Feed
                </span>
              </div>
              <div className="text-gray-500 font-mono text-[10px] tracking-widest" data-id="element-2196">
                AI-VISION-V4.2
              </div>
            </div>

            <img src="https://cdn.magicpatterns.com/uploads/nxBFgVScxoC3BbKSZD5nYJ/file_00000000e8c4722fa0a4be1d5dc1d417.png" alt="AI Toxicity Detection Visualization" className="w-full h-auto object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-700" data-id="element-2197" />

            {/* Overlay UI Elements */}
            <div className="absolute inset-0 pointer-events-none z-10" data-id="element-2198">
              {/* Target Box Animation */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] border border-[#FF0000]/30 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-2199">
                <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#FF0000]" data-id="element-2200" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#FF0000]" data-id="element-2201" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#FF0000]" data-id="element-2202" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#FF0000]" data-id="element-2203" />
              </div>

              {/* Data Stream */}
              <div className="absolute bottom-4 left-4 font-mono text-[10px] text-[#FF0000]/70 leading-tight hidden md:block" data-id="element-2204">
                <div data-id="element-2205">ANALYZING MICRO-EXPRESSIONS...</div>
                <div data-id="element-2206">DETECTING DECEPTION MARKERS...</div>
                <div data-id="element-2207">CORRELATING SPEECH PATTERNS...</div>
                <div className="text-[#FF0000] font-bold mt-1" data-id="element-2208">
                  CONFIDENCE: 99.8%
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-6" data-id="element-2209">
            <h3 className="text-white font-bold uppercase tracking-widest text-sm mb-1" data-id="element-2210">
              AI Forensic Vision
            </h3>
            <p className="text-gray-500 font-mono text-xs" data-id="element-2211">
              Seeing what the human eye misses
            </p>
          </div>
        </motion.div>

        {/* CTA */}
        <div className="mt-12 text-center" data-id="element-2212">
          <div className="inline-block p-1 bg-gradient-to-r from-[#FF0000] via-[#FFD700] to-[#FF0000] rounded-xl shadow-2xl" data-id="element-2213">
            <div className="bg-black rounded-lg px-10 py-8 max-w-2xl" data-id="element-2214">
              <h3 className="text-2xl font-bold text-white mb-3 uppercase tracking-wide" data-id="element-2215">
                Ready for the truth?
              </h3>
              <p className="text-gray-400 mb-8 font-mono text-sm" data-id="element-2216">
                Stop guessing. Start knowing.
              </p>
              <button onClick={() => window.scrollTo({
              top: 0,
              behavior: 'smooth'
            })} className="bg-[#FF0000] text-white px-10 py-4 font-black uppercase tracking-wider hover:bg-red-600 transition-all shadow-[0_0_20px_rgba(255,0,0,0.4)] hover:shadow-[0_0_30px_rgba(255,0,0,0.6)] rounded-lg transform hover:-translate-y-1" data-id="element-2217">
                Start Free Forensic Scan
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const ComparisonRow = ({
  feature,
  competitor,
  us,
  highlight = false,
  isQuote = false
}: {
  feature: string;
  competitor: string;
  us: string;
  highlight?: boolean;
  isQuote?: boolean;
}) => {
  return <div className="grid grid-cols-3 gap-6 group" data-id="element-2218">
      {/* Feature Name */}
      <div className="p-5 flex items-center border-b border-gray-800" data-id="element-2219">
        <span className="text-gray-300 font-bold uppercase text-sm tracking-wide" data-id="element-2220">
          {feature}
        </span>
      </div>

      {/* Competitor Value */}
      <div className="p-5 bg-gray-900/30 border-x border-gray-800 flex items-center justify-center text-center rounded-lg" data-id="element-2221">
        <span className={`text-gray-500 font-mono text-sm ${isQuote ? 'italic' : ''}`} data-id="element-2222">
          {competitor}
        </span>
      </div>

      {/* Our Value */}
      <div className="p-5 bg-[#FF0000]/5 border border-[#FF0000]/30 flex items-center justify-center text-center relative overflow-hidden group-hover:bg-[#FF0000]/10 transition-colors rounded-lg shadow-[0_0_10px_rgba(255,0,0,0.05)]" data-id="element-2223">
        {highlight && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#FF0000]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-2224" />}
        <div className="flex items-center gap-3 relative z-10" data-id="element-2225">
          <span className={`text-white font-bold text-sm md:text-base ${isQuote ? 'font-black text-[#FF0000] italic' : ''}`} data-id="element-2226">
            {us}
          </span>
          <Check size={18} className="text-[#FFD700]" strokeWidth={3} data-id="element-2227" />
        </div>
      </div>
    </div>;
};